import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useUniverseStore } from '../store/universeStore';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { usePlanetView } from '../contexts/PlanetViewContext';
import { useNow } from '../hooks/useNow';
import { usePlanetResource } from '../hooks/usePlanetResource';
import { usePlanetBuildActions } from '../hooks/usePlanetBuildActions';
import UnmetRequirementsGrid, { unmetRequirementTile } from '../components/UnmetRequirementsGrid';
import { rushBuildingUpgrade, type PlanetActionOptions } from '../services/planet.service';
import { purchaseResources } from '../services/premium.service';
import { pendingBuilds, groupPendingBuildsByBuilding } from '../utils/buildQueue';
import { getBuildingIcon } from '../utils/buildingIcons';
import { formatCountdown, formatDuration } from '../utils/formatters';
import BuildableThumb from '../components/BuildableThumb';
import QueueItemCard from '../components/QueueItemCard';
import QueueMaxButton from '../components/QueueMaxButton';
import UnlockSlotsButton from '../components/UnlockSlotsButton';
import { cancelBuildDialogContent } from '../utils/cancelBuildDialog';
import RobotProductionModal from '../components/RobotProductionModal';
import { resourceCostGlyph } from '../components/resourceCostIcons';
import { Button } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import { Skeleton } from '../components/ui/Skeleton';
import { Icon } from '../components/ui/Icon';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import {
    BUILDING_DEFINITIONS,
    BUILDING_NAMES,
    RESOURCE_NAMES,
    RESOURCE_PURCHASE_PRICE,
    RESOURCE_PURCHASE_DAILY_LIMIT,
    RESOURCE_PURCHASE_INCOME_HOURS,
    type BuildingType,
    type BuildingZone,
    type Planet,
    type PlayerRace,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateRushCost,
    computeBuildDurationSeconds,
    computeProjectedBuildingLevels,
    extractBuildingLevels,
    resourcePurchaseDailyCount,
} from '@kaeliux/game-engine';

const ZONE_TITLES: Record<BuildingZone, string> = {
    resource: 'Resource Zone',
    industry: 'Industry Zone',
    military: 'Military Zone',
};

const ZONE_SUBLINK_CLASSES = 'text-content-muted hover:text-content-primary text-label underline';

function isBuildingZone(value: string | undefined): value is BuildingZone {
    return value === 'resource' || value === 'industry' || value === 'military';
}

interface QueuedUpgradeRowProps {
    planet: Planet;
    race: PlayerRace | undefined;
    event: TickCheckpointEventOf<'buildingBuild'>;
    now: number;
    options: PlanetActionOptions;
}

/**
 * A single queued/building entry within a building's own card. Always shows
 * the countdown to the event's actual (cumulative) end date — never the
 * start date, which for anything but the first item in a zone equals the
 * previous item's end date and tells the player nothing new.
 */
function QueuedUpgradeRow({ planet, race, event, now, options }: QueuedUpgradeRowProps) {
    const { openDialog } = useConfirmDialog();
    const view = usePlanetView();
    const [isCancelling, setIsCancelling] = useState(false);
    const [isRushing, setIsRushing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);

    const def = BUILDING_DEFINITIONS[event.eventDetails.buildingType];
    const started = event.eventDetails.startDate.getTime() <= now;
    const remainingMs = event.endDate.getTime() - now;
    const totalMs = event.endDate.getTime() - event.eventDetails.startDate.getTime();
    // Only a started item has an actual timer running — a queued-but-not-yet-
    // started item is still waiting behind its predecessor, so its own
    // progress bar stays at 0 rather than implying movement that isn't there.
    const progress = started ? Math.min(1, Math.max(0, (now - event.eventDetails.startDate.getTime()) / Math.max(1, totalMs))) : 0;
    // A not-yet-started item only has its OWN duration collapsed to zero by a
    // rush — it still waits for whatever's ahead of it in the queue — so
    // pricing it off the full remaining-to-completion time (which includes
    // that wait) would charge for wall-clock time the rush can't actually
    // remove (round 3 review, finding #2).
    const rushCost = calculateRushCost(started ? remainingMs / 1_000 : event.eventDetails.durationSeconds);
    // Rushing a not-yet-started item collapses its own duration to zero
    // without completing it early (it still waits behind its predecessor) —
    // once that's happened there's nothing left to shorten, so a second
    // rush would just be a wasted charge. See tickEvents.ts's rush*Event docs.
    const alreadyRushed = event.eventDetails.durationSeconds === 0;

    async function doCancel() {
        setIsCancelling(true);
        setError(null);
        try {
            await view.cancelBuildingUpgrade(planet, event.eventId, options);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Cancel failed');
        } finally {
            setIsCancelling(false);
        }
    }

    async function doRush() {
        setIsRushing(true);
        setError(null);
        try {
            const { planet: updated, premium } = await rushBuildingUpgrade(planet.id, event.eventId, options);
            usePlanetStore.setState({ currentPlanet: updated });
            usePlayerStore.setState({ premium });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Rush failed');
        } finally {
            setIsRushing(false);
        }
    }

    function requestCancel() {
        openDialog({
            title: `Cancel ${def.name} upgrade?`,
            ...cancelBuildDialogContent(planet, event, now, options.resourceMultiplier),
            confirmLabel: 'Cancel upgrade',
            danger: true,
            onConfirm: () => { doCancel().catch(() => { }); },
        });
    }

    function requestRush() {
        openDialog({
            title: `Rush ${def.name} upgrade?`,
            message: started
                ? `Instantly complete this for ${rushCost} Dark Matter. This is non-refundable.`
                : `Remove this item's own build time for ${rushCost} Dark Matter. It will still wait for the item ahead of it in the queue. This is non-refundable.`,
            confirmLabel: 'Rush',
            onConfirm: () => { doRush().catch(() => { }); },
        });
    }

    return (
        <QueueItemCard
            image={
                <img
                    src={getBuildingIcon(
                        event.eventDetails.buildingType,
                        event.eventDetails.targetLevel,
                        view.buildingRace(event.eventDetails.buildingType) ?? race,
                    )}
                    alt=""
                    className="w-full h-full object-cover rounded-control bg-space-950"
                />
            }
            overlayLabel={`Level ${event.eventDetails.targetLevel}`}
            name={def.name}
            statusText={`${started ? 'Upgrading…' : 'Queued — completes in'} ${formatCountdown(remainingMs)}`}
            progress={progress}
            error={error}
            isCancelling={isCancelling}
            onCancel={view.canManage ? requestCancel : undefined}
            {...(view.allowRush ? {
                rushCost,
                isRushing,
                rushDisabled: darkMatter < rushCost || alreadyRushed,
                rushDisabledReason: alreadyRushed ? 'Rushed' : undefined,
                onRush: requestRush,
            } : {})}
        />
    );
}

interface BuildingRowProps {
    planet: Planet;
    buildingType: BuildingType;
    race: PlayerRace | undefined;
    scienceLevels: Partial<Record<string, number>>;
    projectedLevels: Record<BuildingType, number>;
    queuedEvents: TickCheckpointEventOf<'buildingBuild'>[];
    queueFull: boolean;
    now: number;
    options: PlanetActionOptions;
    speedMultiplier: number;
    highlighted?: boolean;
}

/** Buildings whose zone-list card links to their own screen, and the link's wording. */
const BUILDING_LINK_LABELS: Partial<Record<BuildingType, { title: string; sublink: string }>> = {
    laboratory: { title: 'Open Sciences', sublink: 'Open Sciences →' },
    shipyard: { title: `Open ${BUILDING_NAMES.shipyard}`, sublink: `Open ${BUILDING_NAMES.shipyard} →` },
    techCenter: { title: `Open ${BUILDING_NAMES.techCenter}`, sublink: `Open ${BUILDING_NAMES.techCenter} →` },
    recyclingPlant: { title: `Open ${BUILDING_NAMES.recyclingPlant}`, sublink: `Open ${BUILDING_NAMES.recyclingPlant} →` },
};

function BuildingRow({ planet, buildingType, race: viewerRace, scienceLevels, projectedLevels, queuedEvents, queueFull, now, options, speedMultiplier, highlighted }: BuildingRowProps) {
    const view = usePlanetView();
    const race = view.buildingRace(buildingType) ?? viewerRace;
    const buildingLevels = useMemo(() => extractBuildingLevels(planet.buildings), [planet.buildings]);
    const [showRobotInfo, setShowRobotInfo] = useState(false);
    const isRobotBuilding = buildingType === 'robotFactory' || buildingType === 'automationCenter';

    const projectedLevel = projectedLevels[buildingType] ?? planet.buildings[buildingType]?.level ?? 0;
    const {
        def,
        level,
        nextCost,
        atMax,
        unmetRequirements,
        disabled,
        disabledReason,
        isRequesting,
        error,
        handleUpgrade,
        queueMax,
        isQueueingMax,
        handleQueueMax,
        isDestroying,
        destroyError,
        requestDestroy,
    } = usePlanetBuildActions({
        planet,
        buildingType,
        scienceLevels,
        projectedLevel,
        queuedEvents,
        queueFull,
        options,
    });

    const nextTimeSeconds = atMax
        ? null
        : computeBuildDurationSeconds(buildingType, projectedLevel + 1, buildingLevels.robotFactory ?? 0, speedMultiplier);

    const linkLabels = BUILDING_LINK_LABELS[buildingType];
    const buildingLink = linkLabels ? view.screenRoute(buildingType) : undefined;

    const buildingImage = buildingLink && linkLabels ? (
        <Link
            to={buildingLink}
            title={linkLabels.title}
            className="block w-full h-full rounded-control overflow-hidden hover:opacity-80 hover:ring-2 hover:ring-[color:var(--zone-accent)] transition"
        >
            <img
                src={getBuildingIcon(buildingType, level, race)}
                alt=""
                className="w-full h-full object-cover bg-space-950"
            />
        </Link>
    ) : (
        <img
            src={getBuildingIcon(buildingType, level, race)}
            alt=""
            className="w-full h-full object-cover rounded-control bg-space-950"
        />
    );

    return (
        <Panel
            variant="solid"
            padding="md"
            className="flex flex-col gap-3"
            data-tutorial-id={`building-${buildingType}`}
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1 min-w-0 flex items-start gap-3">
                    <BuildableThumb
                        image={buildingImage}
                        countBadge={level}
                        countTitle={`Level ${level}`}
                        cost={atMax ? {} : nextCost}
                        costLabel={atMax ? undefined : 'Next level'}
                        progressPct={(level / def.maxLevel) * 100}
                        progressTitle={`Level ${level} / ${def.maxLevel}`}
                        highlighted={highlighted}
                    />
                    <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-title text-content-primary">{def.name}</h3>
                            <span className="text-body text-content-muted">
                                Level {level}{queuedEvents.length > 0 ? ` (+${queuedEvents.length})` : ''}
                            </span>
                            {buildingLink && linkLabels && level >= 1 && (
                                <Link to={buildingLink} className={ZONE_SUBLINK_CLASSES}>
                                    {linkLabels.sublink}
                                </Link>
                            )}
                            {isRobotBuilding && level >= 1 && (
                                <button
                                    type="button"
                                    onClick={() => setShowRobotInfo(true)}
                                    title="How this affects resource income"
                                    className="w-5 h-5 flex items-center justify-center rounded-full border border-hairline-strong text-content-muted hover:text-content-primary hover:border-content-secondary text-label transition-colors"
                                >
                                    i
                                </button>
                            )}
                        </div>
                        <p className="text-body text-content-muted">{def.description}</p>
                        {!atMax && nextTimeSeconds !== null && (
                            <p className="text-body text-content-secondary mt-1">
                                <span className="text-content-muted mr-2">Next:</span>{formatDuration(nextTimeSeconds)}
                            </p>
                        )}
                        {error && <p className="text-micro text-status-danger mt-1">{error}</p>}
                        {destroyError && <p className="text-micro text-status-danger mt-1">{destroyError}</p>}
                    </div>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-1">
                    {view.canManage && (
                        <div className="flex gap-2">
                            <Button variant="primary" size="md" onClick={handleUpgrade} disabled={disabled}>
                                {isRequesting ? 'Adding…' : atMax ? 'Max' : 'Upgrade'}
                            </Button>
                            {view.allowDestroy && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={requestDestroy}
                                    disabled={level < 1 || isDestroying || isRequesting || isQueueingMax}
                                    title={level < 1 ? 'No level to destroy' : undefined}
                                >
                                    {isDestroying ? 'Destroying…' : 'Destroy level'}
                                </Button>
                            )}
                        </div>
                    )}
                    {disabledReason && !isRequesting && (
                        <span className="text-micro text-content-muted">{disabledReason}</span>
                    )}
                    <QueueMaxButton
                        preview={queueMax}
                        loading={isQueueingMax}
                        disabled={isRequesting || isQueueingMax || isDestroying}
                        onQueue={() => { handleQueueMax().catch(() => { }); }}
                    />
                </div>
            </div>
            {unmetRequirements.length > 0 && !isRequesting && (
                <UnmetRequirementsGrid tiles={unmetRequirements.map((req) => unmetRequirementTile(req, viewerRace))} />
            )}
            {queuedEvents.length > 0 && (
                <div className="flex flex-wrap gap-3 border-t border-space-700 pt-2">
                    {queuedEvents.map((event) => (
                        <QueuedUpgradeRow
                            key={event.eventId}
                            planet={planet}
                            race={viewerRace}
                            event={event}
                            now={now}
                            options={options}
                        />
                    ))}
                </div>
            )}
            {isRobotBuilding && (
                <RobotProductionModal
                    open={showRobotInfo}
                    robotFactoryLevel={buildingLevels.robotFactory ?? 0}
                    automationCenterLevel={buildingLevels.automationCenter ?? 0}
                    onClose={() => setShowRobotInfo(false)}
                />
            )}
        </Panel>
    );
}

/**
 * Premium "Buy Resources" widget — instantly grants
 * RESOURCE_PURCHASE_INCOME_HOURS worth of this planet's own hourly income
 * in each of metal/minerals/gas for RESOURCE_PURCHASE_PRICE Dark Matter,
 * capped at RESOURCE_PURCHASE_DAILY_LIMIT per planet per day. Shown only on
 * the Resource Zone, mirroring where the original "Buy Resources" premium
 * action lived (docs/old-implementation/zone_resource.php).
 */
function ResourcePackWidget({ planet, now, options }: { planet: Planet; now: number; options: PlanetActionOptions }) {
    const { openDialog } = useConfirmDialog();
    const darkMatter = usePlayerStore((s) => s.premium?.darkMatter ?? 0);
    const [isBuying, setIsBuying] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const purchasesUsed = resourcePurchaseDailyCount(planet.resourcePurchaseDaily, new Date(now));
    const purchasesLeft = Math.max(0, RESOURCE_PURCHASE_DAILY_LIMIT - purchasesUsed);
    const resetMs = purchasesLeft <= 0 && planet.resourcePurchaseDaily
        ? planet.resourcePurchaseDaily.resetAt.getTime() - now
        : null;
    const affordable = darkMatter >= RESOURCE_PURCHASE_PRICE;

    // Real income accrues at rate × resourceMultiplier (resourceEngine.ts's
    // calculatePlanetTick), not the raw cached rate — the pack has to match
    // that or it silently under-grants on any universe above 1× (round 3
    // review, finding #1).
    const grant = {
        metal: Math.floor(planet.resources.metal.rate * RESOURCE_PURCHASE_INCOME_HOURS * options.resourceMultiplier),
        minerals: Math.floor(planet.resources.minerals.rate * RESOURCE_PURCHASE_INCOME_HOURS * options.resourceMultiplier),
        gas: Math.floor(planet.resources.gas.rate * RESOURCE_PURCHASE_INCOME_HOURS * options.resourceMultiplier),
    };
    // Storage-clamped preview — mirrors applyResourcePurchase's own
    // Math.min(current + grant, capacity) so the player sees what they will
    // actually receive, not the raw uncapped grant.
    const effectiveGrant = {
        metal: Math.max(0, Math.min(grant.metal, planet.resources.metal.capacity - planet.resources.metal.current)),
        minerals: Math.max(0, Math.min(grant.minerals, planet.resources.minerals.capacity - planet.resources.minerals.current)),
        gas: Math.max(0, Math.min(grant.gas, planet.resources.gas.capacity - planet.resources.gas.current)),
    };
    const wouldWasteEverything = effectiveGrant.metal <= 0 && effectiveGrant.minerals <= 0 && effectiveGrant.gas <= 0;
    const wouldWastePartial = !wouldWasteEverything
        && (effectiveGrant.metal < grant.metal || effectiveGrant.minerals < grant.minerals || effectiveGrant.gas < grant.gas);
    const disabled = isBuying || purchasesLeft <= 0 || !affordable || wouldWasteEverything;

    async function handleBuy() {
        setIsBuying(true);
        setError(null);
        try {
            const { planet: updated, premium } = await purchaseResources(planet.id, options);
            usePlanetStore.setState({ currentPlanet: updated });
            usePlayerStore.setState({ premium });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Purchase failed');
        } finally {
            setIsBuying(false);
        }
    }

    function requestBuy() {
        openDialog({
            title: 'Buy a Resource Pack?',
            message: `Instantly add ${grant.metal.toLocaleString()} ${RESOURCE_NAMES.metal}, ${grant.minerals.toLocaleString()} ${RESOURCE_NAMES.minerals}, ${grant.gas.toLocaleString()} ${RESOURCE_NAMES.gas} for ${RESOURCE_PURCHASE_PRICE} Dark Matter. This is non-refundable.`,
            warning: wouldWastePartial
                ? 'Storage is near capacity — part of this pack will overflow and be lost.'
                : undefined,
            confirmLabel: 'Buy',
            onConfirm: () => { handleBuy().catch(() => { }); },
        });
    }

    return (
        <Panel variant="accent" padding="md" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
                <h2 className="text-title text-content-primary flex items-center gap-2">
                    <Icon name="dark-matter" size={16} className="text-[color:var(--zone-accent)]" />
                    Resource Pack
                </h2>
                <p className="text-body text-content-secondary flex items-center gap-1.5 flex-wrap">
                    <span>Instantly add {RESOURCE_PURCHASE_INCOME_HOURS}h of income:</span>
                    <span className="inline-flex items-center gap-1">{resourceCostGlyph('metal')} {grant.metal.toLocaleString()}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">{resourceCostGlyph('minerals')} {grant.minerals.toLocaleString()}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">{resourceCostGlyph('gas')} {grant.gas.toLocaleString()}</span>
                </p>
                {wouldWastePartial && (
                    <p className="text-micro text-status-warning mt-1" data-testid="resource-pack-overflow-warning">
                        Storage is near capacity — part of this pack will overflow and be lost.
                    </p>
                )}
                <p className="text-micro text-content-muted mt-1">
                    {purchasesLeft} / {RESOURCE_PURCHASE_DAILY_LIMIT} left today
                    {resetMs !== null && ` — resets in ${formatCountdown(resetMs)}`}
                </p>
                {error && <p className="text-micro text-status-danger mt-1" role="alert">{error}</p>}
            </div>
            <Button
                variant="accent"
                size="md"
                onClick={requestBuy}
                disabled={disabled}
                className="shrink-0"
                data-testid="buy-resource-pack"
            >
                {isBuying ? 'Buying…' : purchasesLeft <= 0 ? 'Limit reached' : wouldWasteEverything ? 'Storage full' : `Buy for ${RESOURCE_PURCHASE_PRICE} Dark Matter`}
            </Button>
        </Panel>
    );
}

interface ZoneBuildingsViewProps {
    zone: BuildingZone;
    planet: Planet | null;
    isLoading: boolean;
    /** Replaces the default "Zone" page title, e.g. to name the Team planet. */
    title?: string;
}

/**
 * One zone's building list. The planet comes in as a prop and everything
 * else (science levels, queue limit, mutations, links, permissions) from
 * `usePlanetView`, so the personal `ZonePage` and the Team planet's zone
 * page share this exactly.
 */
export function ZoneBuildingsView({ zone, planet, isLoading, title }: ZoneBuildingsViewProps) {
    const view = usePlanetView();
    const { profile } = useAuthStore();
    const { resourceMultiplier, speedMultiplier, sunAges } = useUniverseStore();
    const now = useNow();
    const [searchParams] = useSearchParams();
    const highlightBuildingType = searchParams.get('highlight');

    // Issue #162: scroll a `?highlight=` target into view once the building
    // cards first render. Keyed on this boolean rather than `planet`/`sciences`
    // themselves so a background refetch doesn't re-scroll the player.
    const cardsRendered = !isLoading && view.ready;
    useEffect(() => {
        if (!cardsRendered || !highlightBuildingType) return;
        document.querySelector(`[data-tutorial-id="building-${CSS.escape(highlightBuildingType)}"]`)
            ?.scrollIntoView({ block: 'center' });
    }, [highlightBuildingType, cardsRendered]);

    const scienceLevels = view.scienceLevels;
    const allPendingBuilds = useMemo(() => pendingBuilds(planet), [planet]);
    const eventsByBuilding = useMemo(() => groupPendingBuildsByBuilding(allPendingBuilds), [allPendingBuilds]);
    const projectedLevels = useMemo(
        () => (planet ? computeProjectedBuildingLevels(planet, planet.tickCheckpoints ?? []) : ({} as Record<BuildingType, number>)),
        [planet]
    );
    const queueLimit = view.buildingQueueLimit;
    const queueFull = allPendingBuilds.length >= queueLimit;

    const options: PlanetActionOptions = useMemo(
        () => ({ resourceMultiplier, sunAges, speedMultiplier }),
        [resourceMultiplier, sunAges, speedMultiplier]
    );

    if (isLoading || !planet || !view.ready) {
        return (
            <div className="min-h-screen bg-space-900 flex flex-col items-center justify-center gap-3 px-4 w-full max-w-md mx-auto" style={zoneAccentStyle(zone)}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="block" height={96} className="w-full" />
                <Skeleton variant="block" height={96} className="w-full" />
                <Skeleton variant="block" height={96} className="w-full" />
            </div>
        );
    }

    const buildings = (Object.values(BUILDING_DEFINITIONS) as typeof BUILDING_DEFINITIONS[BuildingType][])
        .filter((def) => def.zone === zone);

    return (
        <div className="bg-transparent flex flex-col" style={zoneAccentStyle(zone)}>
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h1 className="text-display text-content-primary">{title ?? ZONE_TITLES[zone]}</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-body text-content-muted" data-testid="zone-queue-count">
                            Queue: {allPendingBuilds.length} / {queueLimit}
                        </span>
                        {queueFull && view.canManage && view.canUnlockMoreSlots && <UnlockSlotsButton />}
                        <Link to={view.homeRoute} className={linkButtonClasses('secondary')}>← Back to Planet</Link>
                    </div>
                </div>
                {zone === 'resource' && view.kind === 'personal' && <ResourcePackWidget planet={planet} now={now} options={options} />}
                <div className="flex flex-col gap-3">
                    {buildings.map((def) => (
                        <BuildingRow
                            key={def.id}
                            planet={planet}
                            buildingType={def.id}
                            race={profile?.race}
                            scienceLevels={scienceLevels}
                            projectedLevels={projectedLevels}
                            queuedEvents={eventsByBuilding[def.id] ?? []}
                            queueFull={queueFull}
                            now={now}
                            options={options}
                            speedMultiplier={speedMultiplier}
                            highlighted={def.id === highlightBuildingType}
                        />
                    ))}
                </div>
            </main>
        </div>
    );
}

export default function ZonePage() {
    const { zoneId } = useParams();
    const { profile } = useAuthStore();
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);

    const planetId = activePlanetId ?? profile?.mainPlanetId ?? '';

    if (!isBuildingZone(zoneId)) {
        return <Navigate to="/planet" replace />;
    }

    if (!profile || !planetId) {
        return (
            <div className="min-h-screen bg-space-900 flex flex-col items-center justify-center gap-3 px-4 w-full max-w-md mx-auto">
                <Skeleton variant="text" width="40%" height={28} />
                <Skeleton variant="block" height={96} className="w-full" />
            </div>
        );
    }

    return <PersonalZonePage zone={zoneId} />;
}

function PersonalZonePage({ zone }: { zone: BuildingZone }) {
    const { state: { planet, isLoading } } = usePlanetResource();
    return <ZoneBuildingsView zone={zone} planet={planet} isLoading={isLoading} />;
}
